import { defineStore } from "pinia";
import { keepOperatorSession, readAssistClaims, takeOperatorSession } from "@/auth/assist";
import { fetchAvatar, fetchDeskDisplay } from "@/auth/profile";
import { createSessionTimers, endSessionOnServer, refreshSessionToken } from "@/auth/refresh";
import { clear as clearToken, getRefreshToken, getToken, setRefreshToken, setToken } from "@/auth/session";
import { SessionVerifyError, verifySessionToken, type VerifiedSession } from "@/auth/verify";
import { navForApp } from "@/nav/navForApp";
import { signInWithPassword } from "@/auth/signIn";
import { fetchIdentityToken, isIdentityToken, signInWithIdentity, signOutIdentity } from "@/auth/identity";
import { fetchSignInPhoto } from "@/auth/signInMethods";
import { signInWithPasskey as passkeyCeremony } from "@/auth/passkey";

import type { HostAssisting, HostUser } from "@/state/globalState";
import type { NavBarItem } from "@/app/types";

/**
 * The shell's one session: who is signed in.
 *
 *   resolving ─► signed-out ─► signing-in ─► ready (a verified session token in auth/session.ts)
 *                           │             └► error
 *                           └► no-access          (signed in, not part of this app)
 *
 * The credential is the EdDSA session JWT, verified here with jose against Pinksy's JWKS
 * (auth/verify.ts) before anything trusts its `sub`; the navigation comes from the app's own
 * manifest (nav/navForApp.ts). A live session keeps two timers (auth/refresh.ts): renew ahead of
 * `exp`, lapse at `exp`.
 *
 * 🔴 **`unavailable` IS GONE, AND SO IS THE QUESTION IT ANSWERED** (D118, PNK-121). This store used
 * to open on "which tenant is this", and `unavailable` was where a boot ended when the hostname
 * matched no row, matched a DISABLED one, or matched one the provisioner had not finished. There is
 * no row to miss: the app is the bundle that is running. `resolving` is kept — it is the state
 * before `resumeFromToken()` has said whether this tab already holds a session, which is a real
 * question with a real answer pending.
 */
export type SessionStatus =
  | "resolving"
  | "signed-out"
  | "signing-in"
  | "no-access"
  | "ready"
  | "error";

interface SessionState {
  status: SessionStatus;
  errorMessage: string | null;
  user: HostUser | null;
  navItems: NavBarItem[];
  /** The session token's `exp` claim (ms epoch); null for a token without one. */
  tokenExpiryMs: number | null;
  /**
   * 🔴 Set only while an OPERATOR is driving this session as somebody else (D34). Null on every
   * ordinary session, which is nearly all of them. Never persisted: it describes a credential this
   * tab is holding, and a remembered banner over an ordinary session would be a lie in the most
   * alarming possible direction.
   */
  assisting: HostAssisting | null;
}

/** One pair of timers for the shell — outside the reactive state, so a `$reset()` never orphans a live timer. */
const timers = createSessionTimers();

/** How one renewal ended: `refused` is the server saying the session is over; `failed` is anything else. */
type Renewal = "renewed" | "refused" | "failed";

/**
 * 🔴 **ONE RENEWAL AT A TIME** (PNK-211). The renewal timer and the lapse can both be due at once —
 * that is what waking a laptop does — and a refresh token is single-use, so a second renewal racing
 * the first presents a spent token and is refused. Everybody asking while one is out waits for it.
 */
let renewing: Promise<Renewal> | null = null;

/**
 * 🔴 **A LAPSE TRIES AGAIN BEFORE IT SIGNS ANYBODY OUT** (PNK-211). The pause after each failed try.
 * A Mac waking from sleep fires the lapse before its Wi-Fi is back, so the first try fails for a
 * reason that has nothing to do with the session; about a minute covers the reconnect.
 */
export const LAPSE_RETRY_MS = [5_000, 15_000, 30_000] as const;

export const useSessionStore = defineStore("shell-session", {
  state: (): SessionState => ({
    status: "resolving",
    errorMessage: null,
    user: null,
    navItems: [],
    tokenExpiryMs: null,
    assisting: null,
  }),
  getters: {
    /** Whether the sign-in form should be offered: the tab has finished saying whether it already holds a session. */
    canSignIn(state): boolean {
      return state.status !== "resolving";
    },
  },
  actions: {
    /**
     * The app is open for business. It used to be `setTenant(host, resolved, isDevHost)`, called with
     * whatever `GET /tenant-apps/resolve` answered, and its whole job was to decide between
     * `signed-out` and `unavailable`. With one app there is nothing to decide (D118) — but the call
     * stays, because the transition out of `resolving` is still real and `boot.ts` is still the one
     * place that makes it.
     */
    setApp() {
      this.status = "signed-out";
    },

    /**
     * 🔴 **THE APPLICATION'S OWN SIGN-IN** (D53). `POST /auth/sign-in` — an address and a password,
     * verified by Pinksy against its own `users` row, answered with a session Pinksy signed.
     *
     * Devin: *"disconnect all the passport pieces from the application… once we've created a user
     * identity."* This is that identity, and since PNK-106 it is the only door — the Passport's
     * development mint went with the app it belonged to.
     *
     * 🔴 **The password is passed, used, and not kept.** It is never put on the store, never on the
     * global state, never in a URL, and the failure path holds only the server's one sentence — which
     * is the same sentence for a wrong password and an unknown address, deliberately (sign-in.ts).
     */
    async signInWithPassword(email: string, password: string): Promise<void> {
      // Belt and braces: the Login screen is not drawn while the store is still `resolving`, but a
      // store that would POST a password before it knows whether this tab already holds a session is
      // the wrong default to leave lying about.
      if (!this.canSignIn) return;
      if (this.status === "signing-in") return;
      this.errorMessage = null;
      this.status = "signing-in";

      // 🔴 **BETTER AUTH FIRST** (D84). Devin: *"I think we should handle the auth with
      // better-auth."* The long-lived half of the session is now an **httpOnly cookie** the browser
      // holds and script cannot read, and `GET /identity/token` turns it into a fifteen-minute
      // bearer token whenever one is needed — so there is no refresh token to store, rotate or lose.
      const identity = await signInWithIdentity(email, password);
      if (identity.ok) {
        await this.establishSession(identity.token, null);
        return;
      }

      // 🔴 The D53 endpoint, still here on purpose, and this is the paragraph that says when it goes.
      // Every person was backfilled into Better Auth (`tools/backfill-auth.ts`) with the password
      // they already had, so this should never be reached — but "should never" is not "cannot", and
      // a migration that leaves no way in if the new path is misconfigured is a migration nobody can
      // deploy on a Friday. Delete this fallback, and `auth/signIn.ts` with it, once a week has gone
      // by with nothing reaching it.
      const legacy = await signInWithPassword(email, password);
      if (!legacy.ok) {
        // The identity refusal is the one shown: it is the path that is meant to work, and its
        // sentence is the same for a wrong password and an unknown address, deliberately.
        this.fail(identity.message);
        return;
      }
      await this.establishSession(legacy.token, legacy.refreshToken);
    },

    /**
     * 🔴 **SIGNING IN WITH A PASSKEY** (PNK-137).
     *
     * It lives in the STORE, next to the password path, rather than in `Login.vue`,
     * because the status machine is what the screen reads: `busy` is
     * `status === "signing-in"`, and a ceremony driven from the component would leave
     * the form live and submittable while the browser's own prompt was open.
     *
     * The ceremony itself is `auth/passkey.ts`. By the time this is called the session
     * COOKIE is already set — Better Auth set it when it verified the signature — so
     * the only thing left is the same step the password path ends with: turn the cookie
     * into the fifteen-minute bearer token the MFEs are handed, and establish.
     *
     * 🔴 A dismissed prompt returns to `signed-out` and says NOTHING. It is the
     * commonest way this ends, and `fail()` would paint the screen red for somebody who
     * simply changed their mind — then the sign-in form would be wearing an error it
     * cannot explain.
     */
    async signInWithPasskey(): Promise<void> {
      if (!this.canSignIn) return;
      if (this.status === "signing-in") return;
      this.errorMessage = null;
      this.status = "signing-in";

      const outcome = await passkeyCeremony();
      if (!outcome.ok) {
        if (outcome.cancelled) this.status = "signed-out";
        else this.fail(outcome.message);
        return;
      }

      const token = await fetchIdentityToken();
      if (token === null) {
        // The signature verified and the cookie was set, but the token exchange failed
        // — a working session this tab cannot use. Saying so beats a silent return to a
        // sign-in screen that would now sign them in without asking.
        this.fail("signed in, but this browser could not pick up the session");
        return;
      }
      await this.establishSession(token, null);
    },

    /** Back from the placeholder (or a failed attempt) to the sign-in screen. */
    returnToLogin() {
      this.errorMessage = null;
      this.status = "signed-out";
    },

    fail(message: string) {
      this.errorMessage = message;
      this.status = "error";
    },

    /**
     * Resume from the token this browser holds (auth/session.ts). Returns `false` — leaving `status`
     * untouched — when there is none, or it cannot be made good. A token that could not be checked
     * because the keys were unreachable is kept for a later attempt, and the sign-in screen says why.
     *
     * 🔴 **AN EXPIRED ACCESS TOKEN IS NOT AN ENDED SESSION** (D83). This is the line Devin was
     * actually hitting: *"Every time I refresh the page, it seems like I lose access."* The access
     * token lasts fifteen minutes and the SESSION lasts a day, so somebody who comes back after
     * twenty minutes — a coffee, a meeting, a laptop lid — arrives with an expired token and a
     * perfectly live session, and this used to throw both away and show the sign-in screen. Now the
     * refresh token is spent first, and only a session the SERVER has ended ends here.
     */
    async resumeFromToken(): Promise<boolean> {
      if (!this.canSignIn) return false;
      const token = getToken();
      /**
       * 🔴 **NO STORED TOKEN IS NOT THE SAME AS NO SESSION** (D123, PNK-141).
       *
       * This used to `return false` here, and that is what made Google sign-in look broken. An
       * OAuth sign-in never passes through this tab's JavaScript: Better Auth sets the httpOnly
       * session COOKIE on the callback and 302s the browser back to `/`, so the shell boots fresh
       * with a live session and an empty `localStorage` — and concluded it was signed out, while
       * the cookie sat there unused. The account was linked, the cookie was set, the sign-in had
       * WORKED; the only thing missing was anybody asking.
       *
       * It cost nothing to find because the flow leaves evidence on both sides — an `authAccounts`
       * row for `google` against the right user, and a 302 from the callback — which is how a
       * "nothing happened" bug turns out to have happened entirely.
       *
       * `renewFromCookie()` already did exactly this for a token that had expired. The same call
       * answers "is there a session I have never seen?", and it is the honest question for a tab
       * that has just arrived from somewhere else. It also fixes the quieter version of the same
       * thing: a second tab opened on a signed-in browser.
       *
       * ⚠️ It is a network round trip on every cold boot with no token — including the common one,
       * a genuinely signed-out visitor, who now waits for a 401 before seeing the sign-in screen.
       * That is the cost, it is one request against `/identity/token`, and the alternative is an
       * OAuth flow that silently does nothing.
       */
      if (!token) return this.renewFromCookie();
      try {
        this.activate(await verifySessionToken(token));
        return true;
      } catch (err) {
        if (err instanceof SessionVerifyError && err.reason === "jwks_unreachable") {
          this.fail(err.message);
          return false;
        }
        // Only expiry is worth renewing: a tampered or wrongly-signed token is not a session that
        // timed out, and presenting a refresh token on the back of one would be the wrong instinct.
        if (err instanceof SessionVerifyError && err.reason === "expired" && (await this.renewExpired(token))) return true;
        clearToken();
        return false;
      }
    },

    /**
     * Spend the refresh token for a session whose ACCESS token has already expired (D83).
     *
     * One app issues sessions, so the refresh has one place to go. It used to be chosen from the
     * expired token's own `kid`, because Pinksy and the Passport kept their refresh rows in different
     * databases and the wrong base was a 401 (auth/refresh.ts).
     */
    async renewExpired(expiredToken: string): Promise<boolean> {
      // 🔴 A Better Auth session renews from its COOKIE (D84) — nothing is presented, because the
      // browser already holds the credential and script cannot read it. This is the whole of the
      // renewal story for a session signed in today, and it is smaller than the one it replaces.
      if (isIdentityToken(expiredToken)) return this.renewFromCookie();
      const refreshToken = getRefreshToken();
      if (!refreshToken) return false;
      const result = await refreshSessionToken(refreshToken);
      if (!result.ok) return false;
      // 🔴 The same correction as `renew()`: the rotation has already happened server-side, so the
      // token in hand is spent and the new one is kept BEFORE anything local can throw.
      setRefreshToken(result.refreshToken);
      try {
        const verified = await verifySessionToken(result.token);
        setToken(result.token);
        this.activate(verified);
        return true;
      } catch {
        return false;
      }
    },

    /**
     * A freshly issued token: verify it, hold it for this tab, and go live. `refreshToken` is the
     * opaque single-use credential `renew()` will spend — a sign-in that hands out none (the dev
     * token) simply lets the session lapse at `exp`.
     */
    async establishSession(token: string, refreshToken: string | null = null, durable = true): Promise<boolean> {
      try {
        const verified = await verifySessionToken(token);
        setToken(token, durable);
        setRefreshToken(refreshToken, durable);
        this.activate(verified);
        return true;
      } catch (err) {
        clearToken();
        this.fail(err instanceof Error ? err.message : String(err));
        return false;
      }
    },

    /**
     * Start assisting: keep the operator's own credentials aside, wear the assisted ones (D34).
     *
     * 🔴 The assist is read off the TOKEN, not taken from the caller, so the banner can only ever say
     * what the issuer actually signed. A screen that could be told it is assisting would be a
     * screen that can be told it is not.
     */
    async startAssisting(token: string, subjectName: string | null): Promise<boolean> {
      const claims = readAssistClaims(token);
      if (claims.actingFor === null || claims.subject === null) return false;
      keepOperatorSession(getToken(), getRefreshToken());
      // 🔴 `durable: false` — an assisted session is held for THIS TAB ONLY (D34). Everything else
      // now survives a tab close (D83); wearing somebody else's session must not.
      const established = await this.establishSession(token, null, false);
      if (!established) {
        // The assisted token did not verify: put the operator straight back where they were.
        const kept = takeOperatorSession();
        if (kept.token !== null) await this.establishSession(kept.token, kept.refresh);
        return false;
      }
      this.assisting = {
        operatorPassportId: claims.actingFor,
        subjectPassportId: claims.subject,
        subjectName,
        reason: "",
        until: claims.expiresAt ?? Date.now(),
      };
      window.PinksyGlobalState?.updateState({ assisting: this.assisting });
      return true;
    },

    /** The reason the operator gave, once the endpoint has echoed it back. Drawn, never decided on. */
    noteAssistReason(reason: string) {
      if (this.assisting === null) return;
      this.assisting = { ...this.assisting, reason };
      window.PinksyGlobalState?.updateState({ assisting: this.assisting });
    },

    /**
     * Stop assisting and go back to the operator's own session. 🔴 The banner comes down FIRST and
     * unconditionally: if putting the operator's session back fails, they must be signed out and
     * told, never left wearing somebody else's session with nothing on screen saying so.
     */
    async stopAssisting(): Promise<void> {
      const kept = takeOperatorSession();
      this.assisting = null;
      window.PinksyGlobalState?.updateState({ assisting: null });
      if (kept.token === null || !(await this.establishSession(kept.token, kept.refresh))) {
        this.signOut();
      }
    },

    /** A verified session becomes the live one: user, nav, host state, timers. */
    activate(verified: VerifiedSession) {
      this.user = { userHash: verified.userHash, displayName: verified.displayName, avatar: null };
      // Chrome, not credentials: the chip draws immediately with whatever the token carried, and the
      // name and face arrive when the application answers. A failure here leaves a monogram, never a
      // failed sign-in (auth/profile.ts).
      void this.describeSelf(verified.userHash);
      this.tokenExpiryMs = verified.expiresAtMs;
      this.navItems = navForApp();
      this.errorMessage = null;
      this.status = "ready";
      window.PinksyGlobalState?.updateState({
        token: verified.token,
        tokenExpiry: verified.expiresAtMs,
        permissions: this.navItems,
        user: this.user,
      });
      if (verified.expiresAtMs === null) {
        timers.cancel();
      } else {
        timers.schedule(
          verified.expiresAtMs,
          () => void this.renew(),
          () => void this.expire(),
        );
      }
    },

    /**
     * Ahead of expiry: spend the REFRESH token for a fresh pair and swap both in (the access token
     * alone is not a refresh credential — auth/refresh.ts). Anything but a verified answer
     * leaves the current session as it is; it lapses at `exp` on its own timer.
     */
    async renew(): Promise<boolean> {
      return (await this.renewal()) === "renewed";
    },

    /** `renew()` with the reason it did not, shared by everybody asking while one is out. */
    async renewal(): Promise<Renewal> {
      if (this.status !== "ready") return "refused";
      renewing ??= this.renewOnce().finally(() => {
        renewing = null;
      });
      return renewing;
    },

    /**
     * 🔴 **ANOTHER TAB MAY ALREADY HAVE RENEWED** (PNK-211). Every Pinksy tab keeps the same session in
     * `localStorage` and runs its own timers, so the tab that fires second finds the first one's fresh
     * token there. Spending the refresh token again would be refused — it is single-use — and the
     * lapse would then sign the person out of every tab. A newer token that verifies is adopted.
     */
    async adoptNewer(): Promise<boolean> {
      const stored = getToken();
      if (!stored || stored === window.PinksyGlobalState?.state.token) return false;
      try {
        const verified = await verifySessionToken(stored);
        if (verified.expiresAtMs === null || verified.expiresAtMs <= (this.tokenExpiryMs ?? 0)) return false;
        this.activate(verified);
        return true;
      } catch {
        return false;
      }
    },

    async renewOnce(): Promise<Renewal> {
      if (await this.adoptNewer()) return "renewed";
      const current = getToken();
      if (!current) return "refused";
      // 🔴 D84 — the cookie is the credential; ask for a fresh bearer token and carry on.
      // `fetchIdentityToken` answers null for a refusal and for no network alike, so it is `failed`.
      if (isIdentityToken(current)) return (await this.renewFromCookie()) ? "renewed" : "failed";
      const refreshToken = getRefreshToken();
      if (!refreshToken) return "refused";
      const result = await refreshSessionToken(refreshToken);
      if (!result.ok) {
        // Refused because another tab spent it first: its fresh token is in storage by now.
        if (await this.adoptNewer()) return "renewed";
        return result.reason === "rejected" ? "refused" : "failed";
      }
      /**
       * 🔴 **THE ROTATION HAS ALREADY HAPPENED, SO THE NEW TOKEN IS KEPT BEFORE ANYTHING CAN THROW.**
       *
       * The server rotated the refresh token the moment it answered: the one in hand is SPENT. It
       * used to be stored only on the success path, so a local `verifySessionToken` failure — a
       * clock skew, a JWKS fetch that did not answer — dropped the rotated token on the floor. The
       * next renewal then presented the spent one, and the issuer treats a spent token as THEFT and
       * revokes the whole session (`services/auth/sessions.ts`). A transient local failure ended a
       * live session, silently, and the person was signed out with no reason given. Found by a sweep
       * on 2026-09-12.
       */
      setRefreshToken(result.refreshToken);
      try {
        const verified = await verifySessionToken(result.token);
        setToken(result.token);
        this.activate(verified);
        return "renewed";
      } catch {
        // The session is untouched and still runs to its own `exp`; the next renewal has a token
        // that is actually current, so a passing failure costs one renewal rather than the session.
        return "failed";
      }
    },

    /**
     * 🔴 **RENEW FROM THE COOKIE** (D84). `GET /identity/token` mints a fresh fifteen-minute JWT for
     * whoever the httpOnly session cookie says you are.
     *
     * A refusal means the SESSION has ended — signed out elsewhere, revoked, or past its absolute
     * end — and that is the only thing that should end it here. A network failure returns false and
     * changes nothing: the current token stays, and its own timer decides.
     */
    async renewFromCookie(): Promise<boolean> {
      const token = await fetchIdentityToken();
      if (token === null) return false;
      try {
        const verified = await verifySessionToken(token);
        setToken(token);
        this.activate(verified);
        return true;
      } catch {
        return false;
      }
    },

    /**
     * `exp` has passed: the session is over, and the next click starts a fresh one.
     *
     * 🔴 A LAPSE IS NOT A SIGN-OUT, and the distinction outlived the thing that needed it. The
     * Passport's modules kept an offline attestation queue on the phone, and routing a lapse through
     * `signOut()` deleted a day of taps taken with no signal. Those modules are gone (PNK-106), so
     * there is nothing device-local left to spare — but the session still ends here and not by
     * pretending the person asked it to.
     */
    async expire(): Promise<void> {
      /**
       * 🔴 **TRY TO RENEW BEFORE GIVING UP** (PNK-211). Celia: *"my pinksy keeps login out"*. Timers
       * do not run while a Mac sleeps, so on waking the renewal and the lapse are both overdue and
       * fire together. The renewal is a network call; the lapse used to sign out on the very next
       * tick, before it answered — and signing out ends the session ON THE SERVER, so the renewal
       * that was about to succeed was spent on a session that no longer existed. Every sleep longer
       * than the fifteen-minute access token signed the person out of a day's (or three days')
       * session. Now the lapse is a renewal that tries again, and only a refusal ends the session.
       */
      for (const pause of [0, ...LAPSE_RETRY_MS]) {
        if (pause > 0) await new Promise<void>((resolve) => setTimeout(resolve, pause));
        if (this.status !== "ready") return;
        const outcome = await this.renewal();
        if (outcome === "renewed") return;
        if (outcome === "refused") break;
      }
      if (this.status === "ready") this.signOut();
    },

    /**
     * Signed in, but not part of this app — route to NoAccess and drop the
     * live session locally (so mounted modules lose their credentials)
     * without forgetting the token: "Sign in with a different account" on
     * that screen is the explicit sign-out.
     */
    denyAccess() {
      timers.cancel();
      this.navItems = [];
      this.tokenExpiryMs = null;
      this.status = "no-access";
      window.PinksyGlobalState?.updateState({ token: null, tokenExpiry: null, permissions: null });
    },

    /**
     * Fill in the name and the face the chrome shows. Best-effort, and never fatal.
     *
     * 🔴 **THE APPLICATION ANSWERS ABOUT ITS OWN PEOPLE** (D52, D37). Devin: *"we should have focused
     * on creating the application first and then come back to enhance it with the passport."* Pinksy's
     * own `users` row carries a name and an e-mail; the Passport was asked as well, and carried them
     * only once its owner had filled their own record in — which for every seeded person was never, so
     * the footer read "Signed in" over a uuid while the Users screen said **Devin Butler**. The
     * Passport is gone (PNK-106) and the user row was always the better answer.
     *
     * The photograph is the caller's own (`GET /me/photo`), asked for directly rather than announced
     * by a flag: it 404s cheaply and the chip falls back to the monogram.
     */
    async describeSelf(userHash: string): Promise<void> {
      const desk = await fetchDeskDisplay();
      if (this.user === null || this.user.userHash !== userHash) return;
      if (desk?.displayName) this.user = { ...this.user, displayName: desk.displayName };
      if (desk?.handle) this.user = { ...this.user, handle: desk.handle };
      /**
       * 🔴 **THE SIGN-IN PHOTOGRAPH IS TRIED FIRST, AND IT IS A DIFFERENT THING FROM THE
       * FACE** (D124, PNK-144).
       *
       * Devin: *"what is the profile photo as one thing and then what your sign-in photo
       * is whatever the latest is"* — after being burnt by a site that captured his
       * identity at sign-up and then showed an address he no longer had.
       *
       * So the chrome shows what his provider has RIGHT NOW, which needs no syncing
       * because it is read from a token rewritten on every sign-in. The face served by
       * `GET /me/photo` is the deliberate one — the call sheet, what productions see — and
       * remains the fallback here, because somebody with no connected account still has a
       * face and should still see it.
       */
      const signIn = await fetchSignInPhoto();
      if (signIn.url !== null) {
        if (this.user === null || this.user.userHash !== userHash) return;
        this.user = { ...this.user, avatar: signIn.url };
        window.PinksyGlobalState?.updateState({ user: this.user });
        return;
      }

      const avatar = await fetchAvatar();
      if (avatar !== null) {
        // The session may have ended, or changed person, while the bytes were in flight.
        if (this.user === null || this.user.userHash !== userHash) {
          URL.revokeObjectURL(avatar);
          return;
        }
        this.user = { ...this.user, avatar };
      }
      window.PinksyGlobalState?.updateState({ user: this.user });
    },

    /** An object URL outlives the session that made it unless someone lets it go. */
    releaseAvatar() {
      const avatar = this.user?.avatar;
      if (typeof avatar === "string" && avatar.startsWith("blob:")) URL.revokeObjectURL(avatar);
    },

    signOut() {
      timers.cancel();
      // 🔴 The SESSION ON THE SERVER first, while the token that names it is still in hand (D83/D84).
      // Forgetting the tokens locally is not signing out — the session row, and Better Auth's cookie,
      // would both stay live for their full life. Not awaited: the screen changes immediately, and an
      // unreachable server lapses the session on its own.
      const held = getToken();
      if (held !== null && isIdentityToken(held)) void signOutIdentity();
      else void endSessionOnServer(held);
      clearToken();
      this.releaseAvatar();
      this.user = null;
      this.navItems = [];
      this.tokenExpiryMs = null;
      this.errorMessage = null;
      /**
       * 🔴 **THE ASSIST BANNER GOES WITH THE SESSION IT BELONGS TO** (D34).
       *
       * Only `stopAssisting()` ever cleared this, so signing out WHILE standing on a company left
       * `assisting` set — and `AssistBanner` renders above the `status` v-if chain, so the next
       * person to sign in at that browser was told they were assisting somebody they had never heard
       * of. On a screen whose entire job is to say "you are not yourself right now", saying it
       * falsely is worse than not saying it. Found by a sweep on 2026-09-12.
       */
      this.assisting = null;
      this.status = "signed-out";
      window.PinksyGlobalState?.updateState({ token: null, tokenExpiry: null, permissions: null, user: null, assisting: null });
    },
  },
});
